import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { fetchActivityDashboard } from '../../services/activityDashboardApi';
import {
  ActivityDashboardResponse, ActivityCounts, TenantActivity,
} from '../../types/ActivityDashboard';

const REFRESH_MS = 60_000;
const MIN_ROTATE_SEC = 5;

/** Leser ?rotate=<sek> fra URL-en. 0 = av. */
const parseRotateSeconds = (params: URLSearchParams): number => {
  const sec = parseInt(params.get('rotate') ?? '', 10);
  return Number.isFinite(sec) && sec > 0 ? Math.max(sec, MIN_ROTATE_SEC) : 0;
};

const COLORS = {
  picks: '#3b82f6',
  receipts: '#22c55e',
  countings: '#f59e0b',
  corrections: '#ef4444',
};

const nf = new Intl.NumberFormat('nb-NO');

const formatTime = (iso?: string) =>
  iso ? new Date(iso).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' }) : '—';

/** Prosentvis avvik mot hverdagssnittet fram til samme klokkeslett, null hvis snittet er ~0. */
const trendPercent = (today: number, avg: number): number | null =>
  avg < 0.1 ? null : Math.round(((today - avg) / avg) * 100);

const Trend: React.FC<{ today: number; avg: number }> = ({ today, avg }) => {
  const pct = trendPercent(today, avg);
  const title = 'Snitt pr. hverdag siste 7 dager, fram til samme klokkeslett som nå';
  if (pct === null) {
    return <span className="text-gray-500 text-lg whitespace-nowrap" title={title}>snitt så langt: {avg.toFixed(1)}</span>;
  }
  const up = pct >= 0;
  return (
    <span className={`text-lg font-semibold whitespace-nowrap ${up ? 'text-green-400' : 'text-red-400'}`} title={title}>
      {up ? '▲' : '▼'} {Math.abs(pct)} % <span className="text-gray-500 font-normal">vs snitt så langt ({Math.round(avg)})</span>
    </span>
  );
};

const Tile: React.FC<{ label: string; value: number; sub?: string; today?: number; avg?: number }> =
  ({ label, value, sub, today, avg }) => (
    // Toppjustert med faste høyder på etikett og tall, slik at hovedtallene
    // ligger på linje på tvers av kortene uansett hvor mye bunntekst de har.
    <div className="bg-gray-900 rounded-2xl px-6 py-5 border border-gray-800">
      <div className="text-gray-400 text-xl uppercase tracking-wide truncate">{label}</div>
      <div className="text-white font-bold leading-none my-2" style={{ fontSize: '4.5rem' }}>
        {nf.format(value)}
      </div>
      <div className="flex flex-col gap-1 min-h-[3.75rem]">
        {today !== undefined && avg !== undefined && <Trend today={today} avg={avg} />}
        {sub && <span className="text-gray-500 text-lg">{sub}</span>}
      </div>
    </div>
  );

/** Slår sammen timedata fra flere tenants til én rad pr. time. */
const buildHourlyData = (tenants: TenantActivity[]) => {
  const hours = new Map<number, { picks: number; receipts: number; countings: number; corrections: number }>();
  tenants.forEach((t) =>
    t.hourly.forEach((h) => {
      const row = hours.get(h.hour) ?? { picks: 0, receipts: 0, countings: 0, corrections: 0 };
      row.picks += h.picks;
      row.receipts += h.receipts;
      row.countings += h.countings;
      row.corrections += h.corrections;
      hours.set(h.hour, row);
    })
  );
  const dataHours = Array.from(hours.keys());
  const from = Math.min(6, ...(dataHours.length ? dataHours : [6]));
  const to = Math.max(20, ...(dataHours.length ? dataHours : [20]));
  const result = [];
  for (let h = from; h <= to; h++) {
    result.push({ label: `${String(h).padStart(2, '0')}`, ...(hours.get(h) ?? { picks: 0, receipts: 0, countings: 0, corrections: 0 }) });
  }
  return result;
};

const sumCounts = (tenants: TenantActivity[], pick: (t: TenantActivity) => ActivityCounts): ActivityCounts =>
  tenants.reduce(
    (acc, t) => ({
      pickLists: acc.pickLists + pick(t).pickLists,
      salesOrders: acc.salesOrders + pick(t).salesOrders,
      orderLines: acc.orderLines + pick(t).orderLines,
      receipts: acc.receipts + pick(t).receipts,
      countings: acc.countings + pick(t).countings,
      corrections: acc.corrections + pick(t).corrections,
      pickLines: acc.pickLines + pick(t).pickLines,
      receiptLines: acc.receiptLines + pick(t).receiptLines,
      countLines: acc.countLines + pick(t).countLines,
      correctionLines: acc.correctionLines + pick(t).correctionLines,
    }),
    { pickLists: 0, salesOrders: 0, orderLines: 0, receipts: 0, countings: 0, corrections: 0, pickLines: 0, receiptLines: 0, countLines: 0, correctionLines: 0 }
  );

const tenantStatus = (t: TenantActivity): 'error' | 'active' | 'quiet' => {
  if (!t.available) return 'error';
  if (t.lastActivity && Date.now() - new Date(t.lastActivity).getTime() < 60 * 60 * 1000) return 'active';
  return 'quiet';
};

const ActivityDashboard: React.FC = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const key = params.get('key') ?? '';
  const rotateSec = useMemo(() => parseRotateSeconds(params), [params]);
  const [data, setData] = useState<ActivityDashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<number | null>(null);
  const [rotationPaused, setRotationPaused] = useState(false);
  const [clock, setClock] = useState(new Date());

  // Auto-rotering gjelder kun master-nøkkelen; kunde-nøkkel ser alltid bare sin egen tenant.
  const rotationActive = rotateSec > 0 && data !== null && data.scope !== 'tenant' && !rotationPaused;

  // Tenant-listen leses via ref, så 60-sek-oppdateringen ikke nullstiller rotasjonstimeren.
  const rotationIdsRef = useRef<number[]>([]);
  rotationIdsRef.current = data ? data.tenants.filter((t) => t.available).map((t) => t.tenantId) : [];

  useEffect(() => {
    if (!rotationActive) return;
    const timer = setInterval(() => {
      // Sekvens: Alle → hver tilgjengelig tenant → Alle …
      const sequence: (number | null)[] = [null, ...rotationIdsRef.current];
      setSelectedTenant((current) => sequence[(sequence.indexOf(current) + 1) % sequence.length]);
    }, rotateSec * 1000);
    return () => clearInterval(timer);
  }, [rotationActive, rotateSec]);

  const selectManually = (tenantId: number | null) => {
    setSelectedTenant(tenantId);
    if (rotateSec > 0) setRotationPaused(true);
  };

  const load = useCallback(async () => {
    try {
      const response = await fetchActivityDashboard(key);
      setData(response);
      setError(null);
      setLastFetched(new Date());
    } catch (e: any) {
      setError(e?.response?.status === 401 ? 'Ugyldig dashbord-nøkkel i URL-en' : 'Får ikke kontakt med API-et');
    }
  }, [key]);

  useEffect(() => {
    load();
    const dataTimer = setInterval(load, REFRESH_MS);
    const clockTimer = setInterval(() => setClock(new Date()), 1000);
    return () => {
      clearInterval(dataTimer);
      clearInterval(clockTimer);
    };
  }, [load]);

  if (!key) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center text-gray-300 text-2xl">
        Mangler ?key=… i URL-en
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center text-red-400 text-2xl">
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center text-gray-400 text-2xl">
        Laster aktivitet …
      </div>
    );
  }

  // Kunde-nøkkel gir tenant-scopet respons: visningen låses til tenanten og tenant-raden skjules
  const tenantScoped = data.scope === 'tenant';
  const tenant = tenantScoped
    ? data.tenants[0]
    : selectedTenant !== null ? data.tenants.find((t) => t.tenantId === selectedTenant) : undefined;
  const visibleTenants = tenant ? [tenant] : data.tenants.filter((t) => t.available);
  const today = tenant ? tenant.today : sumCounts(visibleTenants, (t) => t.today);
  const avg = tenant ? tenant.averagePerDay : sumCounts(visibleTenants, (t) => t.averagePerDay);
  const operators = tenant ? tenant.activeOperators : data.activeOperators;
  const hourlyData = buildHourlyData(visibleTenants);

  return (
    <div className="min-h-screen bg-gray-950 text-white px-8 py-6 flex flex-col gap-6">
      {/* Topplinje */}
      <div className="flex items-baseline justify-between">
        <h1 className="text-4xl font-bold">
          Compass WMS — aktivitet i dag
          {tenant && <span className="text-blue-400"> · {tenant.tenantName}</span>}
        </h1>
        <div className="flex items-baseline gap-6 text-gray-400 text-xl">
          {error && <span className="text-red-400">⚠ {error}</span>}
          {!tenantScoped && rotateSec > 0 && (
            <button
              onClick={() => setRotationPaused((p) => !p)}
              className={rotationPaused ? 'text-yellow-400' : 'text-gray-400 hover:text-white'}
              title={rotationPaused ? 'Klikk for å gjenoppta rotering' : 'Klikk for å pause rotering'}
            >
              {rotationPaused ? '⏸ Rotering pauset' : `⟳ Roterer hvert ${rotateSec} s`}
            </button>
          )}
          <span>Oppdatert {lastFetched ? lastFetched.toLocaleTimeString('nb-NO') : '—'}</span>
          <span className="text-white font-mono text-3xl">{clock.toLocaleTimeString('nb-NO')}</span>
        </div>
      </div>

      {/* Nøkkeltall */}
      <div className="grid grid-cols-3 xl:grid-cols-6 gap-4">
        <Tile
          label="Plukklinjer"
          value={today.pickLines}
          today={today.pickLines}
          avg={avg.pickLines}
          sub={`${nf.format(today.pickLists)} plukklister`}
        />
        <Tile
          label="Ordrelinjer"
          value={today.orderLines}
          today={today.orderLines}
          avg={avg.orderLines}
          sub={`${nf.format(today.salesOrders)} salgsordrer`}
        />
        <Tile
          label="Mottakslinjer"
          value={today.receiptLines}
          today={today.receiptLines}
          avg={avg.receiptLines}
          sub={`${nf.format(today.receipts)} varemottak`}
        />
        <Tile
          label="Tellelinjer"
          value={today.countLines}
          today={today.countLines}
          avg={avg.countLines}
          sub={`${nf.format(today.countings)} tellinger`}
        />
        <Tile
          label="Korreksjonslinjer"
          value={today.correctionLines}
          today={today.correctionLines}
          avg={avg.correctionLines}
          sub={`${nf.format(today.corrections)} korreksjoner`}
        />
        <Tile label="Aktive operatører" value={operators} />
      </div>

      {/* Timekurve */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%" minHeight={260}>
          <BarChart data={hourlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
            <XAxis dataKey="label" stroke="#6b7280" tick={{ fontSize: 16 }} />
            <YAxis stroke="#6b7280" tick={{ fontSize: 16 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8 }}
              labelFormatter={(label) => `Kl. ${label}:00`}
            />
            <Legend wrapperStyle={{ fontSize: 16 }} />
            <Bar dataKey="picks" name="Plukk" stackId="a" fill={COLORS.picks} />
            <Bar dataKey="receipts" name="Mottak" stackId="a" fill={COLORS.receipts} />
            <Bar dataKey="countings" name="Telling" stackId="a" fill={COLORS.countings} />
            <Bar dataKey="corrections" name="Korreksjon" stackId="a" fill={COLORS.corrections} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tenant-rad (skjules for kunde-scopet visning) */}
      {!tenantScoped && (
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(data.tenants.length + 1, 7)}, minmax(0, 1fr))` }}>
        <button
          onClick={() => selectManually(null)}
          className={`rounded-2xl border p-4 text-left transition-colors ${
            selectedTenant === null ? 'bg-blue-950 border-blue-500' : 'bg-gray-900 border-gray-800 hover:border-gray-600'
          }`}
        >
          <div className="text-2xl font-bold">Alle</div>
          <div className="text-gray-400 text-lg mt-1">{data.tenants.filter((t) => t.available).length} av {data.tenants.length} tenants OK</div>
        </button>
        {data.tenants.map((t) => {
          const status = tenantStatus(t);
          return (
            <button
              key={t.tenantId}
              onClick={() => selectManually(selectedTenant === t.tenantId ? null : t.tenantId)}
              className={`rounded-2xl border p-4 text-left transition-colors ${
                selectedTenant === t.tenantId ? 'bg-blue-950 border-blue-500' : 'bg-gray-900 border-gray-800 hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block w-3 h-3 rounded-full ${
                    status === 'error' ? 'bg-red-500' : status === 'active' ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                />
                <span className="text-xl font-semibold truncate">{t.tenantName}</span>
              </div>
              {t.available ? (
                <>
                  <div className="text-gray-300 text-lg mt-2">
                    {nf.format(t.today.pickLines)} plukk · {nf.format(t.today.receiptLines)} mottak · {nf.format(t.today.countLines)} telling
                  </div>
                  <div className="text-gray-500 text-base mt-1">
                    {t.activeOperators} operatører · sist {formatTime(t.lastActivity)}
                  </div>
                </>
              ) : (
                <div className="text-red-400 text-lg mt-2">Utilgjengelig</div>
              )}
            </button>
          );
        })}
      </div>
      )}
    </div>
  );
};

export default ActivityDashboard;
